import { isBlockedEmailDomain } from '@mnd-apps/public/lib/emailDomainBlockList';
import { ReCaptchaProvider, useReCaptcha } from '@mnd-apps/public/lib/next-recaptcha-v3';
import { track } from '@mnd-apps/public/lib/tracker';
import { routes } from '@mnd-apps/public/routes';
import { formatUrl } from '@mnd-apps/public/utils/formatUrl';
import logger from '@mnd-apps/public/utils/logger';
import { Button } from '@mnd-frontend/ui/public/components/Button';
import Checkbox from '@mnd-frontend/ui/public/components/Checkbox';
import Icons from '@mnd-frontend/ui/publish/components/Icons';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import {
  GREY_800,
  PURPLE_600,
  RED_700,
  SPACING_1,
  SPACING_2,
  SPACING_3,
} from '@mnd-frontend/ui/shared/tokens';
import isEmail from 'nice-is-email';
import { type FC, type FormEventHandler, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import EmailInput from './EmailInput';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_3};
`;

const EmailInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${SPACING_1};
  justify-content: center;
`;

const MarketingConsent = styled.div`
  display: flex;
  gap: ${SPACING_2};
  color: ${GREY_800};
  font-size: 0.875rem; // token: web/body/sm
`;

const PrivacyPolicy = styled.div`
  color: ${GREY_800}; // token: web/body/sm
  font-size: 0.875rem;
`;

const InlineLink = styled.a`
  color: ${PURPLE_600};
  text-decoration: underline;
`;

const SuccessWrapper = styled.div`
  display: flex;
  gap: ${SPACING_2};
`;

const SuccessCheck = styled(Icons.Check)`
  color: ${RED_700};
`;

type Props = {
  hubspotFormId: string;
};

type FormError = {
  error: string;
  errorCode: string;
};

const NewsletterSignupForm: FC<Props> = ({ hubspotFormId }) => {
  const { showFailureToast } = useToasts();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const { executeRecaptcha } = useReCaptcha();
  const [successfulSubmission, setSuccessfulSubmission] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const validateEmail = (email: string) => {
    if (email.trim().length < 1) {
      setError(t('form.validation.email.required'));
      return false;
    } else if (!isEmail(email)) {
      setError(t('form.validation.email.format'));
      return false;
    } else if (isBlockedEmailDomain(email)) {
      setError(t('form.validation.email.blocked'));
      return false;
    } else {
      setError(undefined);
      return true;
    }
  };

  const handleRecpatchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      logger.error('ReCAPTCHA not available');
      return;
    }
    const token = await executeRecaptcha('NewsletterSubscription');
    return token;
  }, [executeRecaptcha]);

  const trackError = (error_code: string, error_message: string, form_field: string) => {
    track({
      event: 'error_form_submit',
      traits: {
        event_info: {
          form_id: hubspotFormId,
          error_code,
          error_message,
          form_field,
          form_name: 'Newsletter Signup',
          navigation_selection: 'Newsletter Signup',
          user_interaction: 'cta_click',
        },
      },
    });
  };

  const handleOnSubmit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const { email, marketing_communications_opt_in } = Object.fromEntries(
      formData.entries(),
    ) as Record<string, string>;

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);

    const token = await handleRecpatchaVerify();
    try {
      const response = await fetch('/api/form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          hubspotFormId,
          fields: [
            { name: 'email', value: email },
            {
              name: 'marketing_communications_opt_in',
              value: marketing_communications_opt_in ? 'Yes' : 'No',
            },
            { name: 'consent_to_processing', value: true },
          ],
        }),
      });

      setLoading(false);

      if (response.ok) {
        setSuccessfulSubmission(true);
      } else if (response.headers.get('content-type')?.includes('application/json')) {
        const { errors: responseErrors }: { errors: Record<string, FormError[]> } =
          await response.json();
        for (const [field, errors] of Object.entries(responseErrors)) {
          for (const { errorCode, error } of errors) {
            trackError(errorCode, error, field);
          }
        }
        if (responseErrors.email) {
          setError(t('form.validation.email.blocked'));
        } else {
          showFailureToast('Something went wrong');
        }
      } else {
        trackError('unknown', 'Failed to submit form', 'unknown');
      }
    } catch (e) {
      setLoading(false);
      logger.error({ error: e as Error }, 'Something went wrong');
    }
  };

  return (
    <Form onSubmit={handleOnSubmit}>
      <EmailInputWrapper>
        {successfulSubmission ? (
          <SuccessWrapper>
            <p>{t('form.newletter.success')}</p>
            <SuccessCheck />
          </SuccessWrapper>
        ) : (
          <>
            <EmailInput
              placeholder={t('form.placeholder.email')}
              error={error}
              onChange={() => setError(undefined)}
            />
            <Button
              configuration="primary"
              type="submit"
              icon={<Icons.ArrowRight />}
              loading={loading}
              disabled={loading}
              aria-label="Newsletter Signup"
            />
          </>
        )}
      </EmailInputWrapper>
      <MarketingConsent>
        <Checkbox
          name="marketing_communications_opt_in"
          label={t('marketing_consent-newsletter-signup')}
        />
      </MarketingConsent>
      <PrivacyPolicy>
        <Trans
          i18nKey="consent_data_usage-newsletter-signup"
          components={{
            consentLink: (
              <InlineLink
                href={formatUrl(routes.consentPolicy(lang))}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </PrivacyPolicy>
    </Form>
  );
};

const NewsletterSignupFormWrapper: FC<Props> = props => {
  return (
    <ReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
      async={false}
      defer={false}
      className="optanon-category-C0001"
    >
      <NewsletterSignupForm {...props} />
    </ReCaptchaProvider>
  );
};

export default NewsletterSignupFormWrapper;
